/**
 * CEO Command Center v11 - Sync Store
 *
 * Zustand store for managing sync state:
 * - Database initialization
 * - Device identity management
 * - Space management
 * - Peer connections (future)
 * - Sync status
 *
 * @module stores/syncStore
 * @version 11.0.0
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { databaseService, cryptoService } from '@/services/sync';
import type {
  DeviceIdentity,
  Space,
  SpaceMember,
  SyncStatus,
  PeerInfo,
  TableName,
} from '@/types/sync';

// ============================================
// TYPES
// ============================================

interface SyncState {
  // Initialization
  initialized: boolean;
  initializing: boolean;
  initError: string | null;

  // Identity
  identity: DeviceIdentity | null;
  unlocked: boolean;

  // Spaces
  spaces: Space[];
  activeSpaceId: string | null;
  activeSpace: Space | null;

  // Sync status
  status: SyncStatus;
  connectedPeers: Map<string, PeerInfo>;
  pendingChanges: number;
  lastSyncAt: number | null;

  // Error state
  error: string | null;
}

interface SyncActions {
  // Initialization
  init: () => Promise<void>;
  reset: () => void;

  // Identity
  createIdentity: (name: string, passphrase: string) => Promise<DeviceIdentity>;
  unlockIdentity: (passphrase: string) => Promise<boolean>;
  lockIdentity: () => void;

  // Spaces
  loadSpaces: () => Promise<void>;
  createSpace: (name: string, tables?: TableName[]) => Promise<Space>;
  joinSpace: (inviteUrl: string) => Promise<Space>;
  leaveSpace: (spaceId: string) => Promise<void>;
  setActiveSpace: (spaceId: string | null) => void;

  // Space members
  getSpaceMembers: (spaceId: string) => Promise<SpaceMember[]>;
  generateInvite: (spaceId: string) => Promise<string>;

  // Sync (placeholders for Phase 2)
  startSync: () => Promise<void>;
  stopSync: () => void;

  // Utility
  setError: (error: string | null) => void;
  clearError: () => void;
}

type SyncStore = SyncState & SyncActions;

// ============================================
// DEFAULT STATE
// ============================================

const DEFAULT_STATE: SyncState = {
  initialized: false,
  initializing: false,
  initError: null,
  identity: null,
  unlocked: false,
  spaces: [],
  activeSpaceId: null,
  activeSpace: null,
  status: 'disconnected',
  connectedPeers: new Map(),
  pendingChanges: 0,
  lastSyncAt: null,
  error: null,
};

// All tables for default space
const ALL_TABLES: TableName[] = [
  'tasks',
  'deals',
  'clients',
  'owners',
  'cashflow',
  'investors',
  'mau_data',
  'customer_mau',
  'initiatives',
  'board_members',
  'deck_sections',
  'meeting_history',
  'market_news',
  'knowledge_base',
];

// ============================================
// STORE
// ============================================

export const useSyncStore = create<SyncStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      // ============================================
      // INITIALIZATION
      // ============================================

      init: async () => {
        const { initialized, initializing } = get();
        if (initialized || initializing) return;

        set({ initializing: true, initError: null });

        try {
          // Initialize database
          await databaseService.init();

          // Check for existing identity
          const identity = await databaseService.getIdentity();

          // Load spaces if identity exists
          let spaces: Space[] = [];
          if (identity) {
            spaces = await databaseService.getSpaces();
          }

          set({
            initialized: true,
            initializing: false,
            identity,
            spaces,
            // Set first space as active if available
            activeSpaceId: spaces.length > 0 ? spaces[0].id : null,
            activeSpace: spaces.length > 0 ? spaces[0] : null,
          });

          console.log('Sync store initialized', { hasIdentity: !!identity, spaceCount: spaces.length });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to initialize';
          set({ initializing: false, initError: message });
          console.error('Sync store init failed:', error);
          throw error;
        }
      },

      reset: () => {
        set(DEFAULT_STATE);
      },

      // ============================================
      // IDENTITY
      // ============================================

      createIdentity: async (name: string, passphrase: string) => {
        try {
          set({ error: null });
          const identity = await databaseService.createIdentity(name, passphrase);

          // Set identity in state FIRST (required for createSpace)
          set({
            identity,
            unlocked: true,
          });

          // Create default space
          const defaultSpace = await get().createSpace('My Workspace', ALL_TABLES);

          set({
            activeSpaceId: defaultSpace.id,
            activeSpace: defaultSpace,
          });

          return identity;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create identity';
          set({ error: message });
          throw error;
        }
      },

      unlockIdentity: async (passphrase: string) => {
        try {
          set({ error: null });
          const success = await databaseService.unlockIdentity(passphrase);

          if (success) {
            // Load spaces after unlock
            const spaces = await databaseService.getSpaces();
            set({
              unlocked: true,
              spaces,
              activeSpaceId: spaces.length > 0 ? spaces[0].id : null,
              activeSpace: spaces.length > 0 ? spaces[0] : null,
            });
          }

          return success;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to unlock';
          set({ error: message });
          return false;
        }
      },

      lockIdentity: () => {
        set({
          unlocked: false,
          status: 'disconnected',
          connectedPeers: new Map(),
        });
      },

      // ============================================
      // SPACES
      // ============================================

      loadSpaces: async () => {
        try {
          const spaces = await databaseService.getSpaces();
          const { activeSpaceId } = get();

          // Update active space reference
          const activeSpace = activeSpaceId
            ? spaces.find(s => s.id === activeSpaceId) || null
            : spaces[0] || null;

          set({
            spaces,
            activeSpace,
            activeSpaceId: activeSpace?.id || null,
          });
        } catch (error) {
          console.error('Failed to load spaces:', error);
        }
      },

      createSpace: async (name: string, tables: TableName[] = ALL_TABLES) => {
        const { identity } = get();
        if (!identity) {
          throw new Error('No identity - cannot create space');
        }

        try {
          set({ error: null });

          // Generate space encryption key
          const salt = cryptoService.generateSalt();
          const passphrase = cryptoService.generatePassphrase();
          const spaceKey = await cryptoService.deriveKey(passphrase, salt);

          // Create key check (encrypted known value)
          const keyCheckData = new TextEncoder().encode('CEOCC-KEY-CHECK');
          const { ciphertext: keyCheckCipher } = await cryptoService.encrypt(keyCheckData, spaceKey);
          const keyCheck = cryptoService.bytesToBase64(keyCheckCipher);

          // Get device key and encrypt space key
          const deviceKey = databaseService.getDeviceKey();
          if (!deviceKey) {
            throw new Error('Device not unlocked');
          }

          const spaceKeyBytes = await cryptoService.exportKey(spaceKey);
          const { ciphertext: encryptedKeyBytes, nonce } = await cryptoService.encrypt(
            spaceKeyBytes,
            deviceKey
          );

          // Combine nonce + ciphertext for storage
          const encryptedKey = cryptoService.bytesToBase64(
            new Uint8Array([...nonce, ...encryptedKeyBytes])
          );

          const now = Date.now();
          const space: Space = {
            id: cryptoService.randomUUID(),
            name,
            encryptedKey,
            salt: cryptoService.bytesToBase64(salt),
            keyCheck,
            tables,
            createdAt: now,
            joinedAt: now,
          };

          // Save to database
          await databaseService.createSpace(space);

          // Add self as owner
          await databaseService.addSpaceMember({
            spaceId: space.id,
            publicKey: identity.publicKey,
            name: identity.name,
            role: 'owner',
            addedAt: now,
            addedBy: identity.publicKey,
          });

          // Update store
          const spaces = [...get().spaces, space];
          set({ spaces });

          return space;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create space';
          set({ error: message });
          throw error;
        }
      },

      joinSpace: async (inviteUrl: string) => {
        const { identity } = get();
        if (!identity) {
          throw new Error('No identity - create one first');
        }

        try {
          // Parse invite URL
          const match = inviteUrl.match(/ceocc:\/\/join\/(.+)/);
          if (!match) {
            throw new Error('Invalid invite URL format');
          }

          const inviteBase64 = match[1];
          const inviteJson = atob(inviteBase64);
          const invite = JSON.parse(inviteJson) as {
            version: string;
            spaceId: string;
            spaceName: string;
            passphrase: string;
            tables: TableName[];
            inviterName: string;
            inviterKey: string;
            encryptedKey: string;
            salt: string;
            keyCheck: string;
            expiresAt: number;
          };

          // Validate invite
          if (invite.version !== 'v1') {
            throw new Error('Unsupported invite version');
          }

          if (invite.expiresAt < Date.now()) {
            throw new Error('Invite has expired');
          }

          // Check if already have this space
          const existingSpace = get().spaces.find(s => s.id === invite.spaceId);
          if (existingSpace) {
            throw new Error('Already a member of this space');
          }

          // Derive invite key from passphrase
          const inviteSalt = cryptoService.base64ToBytes(invite.salt);
          const inviteKey = await cryptoService.deriveKey(invite.passphrase, inviteSalt);

          // Decrypt the space key
          const encryptedKeyData = cryptoService.base64ToBytes(invite.encryptedKey);
          const nonce = encryptedKeyData.slice(0, 12);
          const ciphertext = encryptedKeyData.slice(12);
          const spaceKeyBytes = await cryptoService.decrypt(ciphertext, nonce, inviteKey);

          // Re-encrypt with our device key
          const deviceKey = databaseService.getDeviceKey();
          if (!deviceKey) {
            throw new Error('Device not unlocked');
          }

          const { ciphertext: ourCiphertext, nonce: ourNonce } = await cryptoService.encrypt(
            spaceKeyBytes,
            deviceKey
          );
          const ourEncryptedKey = cryptoService.bytesToBase64(
            new Uint8Array([...ourNonce, ...ourCiphertext])
          );

          // Create space locally
          const now = Date.now();
          const space: Space = {
            id: invite.spaceId,
            name: invite.spaceName,
            encryptedKey: ourEncryptedKey,
            salt: invite.salt,
            keyCheck: invite.keyCheck,
            tables: invite.tables,
            createdAt: now, // We don't know original creation time
            joinedAt: now,
          };

          // Save to database
          await databaseService.createSpace(space);

          // Add inviter as a member (owner)
          await databaseService.addSpaceMember({
            spaceId: space.id,
            publicKey: invite.inviterKey,
            name: invite.inviterName,
            role: 'owner',
            addedAt: now,
            addedBy: invite.inviterKey,
          });

          // Add ourselves as a member
          await databaseService.addSpaceMember({
            spaceId: space.id,
            publicKey: identity.publicKey,
            name: identity.name,
            role: 'member',
            addedAt: now,
            addedBy: invite.inviterKey,
          });

          // Update store
          const spaces = [...get().spaces, space];
          set({
            spaces,
            activeSpaceId: space.id,
            activeSpace: space,
          });

          return space;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to join space';
          set({ error: message });
          throw error;
        }
      },

      leaveSpace: async (spaceId: string) => {
        try {
          await databaseService.deleteSpace(spaceId);

          const spaces = get().spaces.filter(s => s.id !== spaceId);
          const { activeSpaceId } = get();

          set({
            spaces,
            activeSpaceId: activeSpaceId === spaceId ? (spaces[0]?.id || null) : activeSpaceId,
            activeSpace:
              activeSpaceId === spaceId ? (spaces[0] || null) : get().activeSpace,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to leave space';
          set({ error: message });
          throw error;
        }
      },

      setActiveSpace: (spaceId: string | null) => {
        const { spaces } = get();
        const activeSpace = spaceId ? spaces.find(s => s.id === spaceId) || null : null;
        set({ activeSpaceId: spaceId, activeSpace });
      },

      // ============================================
      // SPACE MEMBERS
      // ============================================

      getSpaceMembers: async (spaceId: string) => {
        return databaseService.getSpaceMembers(spaceId);
      },

      generateInvite: async (spaceId: string) => {
        const { identity, spaces } = get();
        if (!identity) {
          throw new Error('No identity');
        }

        const space = spaces.find(s => s.id === spaceId);
        if (!space) {
          throw new Error('Space not found');
        }

        // Generate a passphrase for the invite
        const invitePassphrase = cryptoService.generatePassphrase(4);

        // Derive a key from the passphrase for encrypting the space key
        const inviteSalt = cryptoService.generateSalt();
        const inviteKey = await cryptoService.deriveKey(invitePassphrase, inviteSalt);

        // Get the space key (decrypted)
        const deviceKey = databaseService.getDeviceKey();
        if (!deviceKey) {
          throw new Error('Device not unlocked');
        }

        // Decrypt the space key
        const encryptedKeyData = cryptoService.base64ToBytes(space.encryptedKey);
        const nonce = encryptedKeyData.slice(0, 12);
        const ciphertext = encryptedKeyData.slice(12);
        const spaceKeyBytes = await cryptoService.decrypt(ciphertext, nonce, deviceKey);

        // Re-encrypt with invite key
        const { ciphertext: inviteCiphertext, nonce: inviteNonce } = await cryptoService.encrypt(
          spaceKeyBytes,
          inviteKey
        );
        const encryptedKeyForInvite = cryptoService.bytesToBase64(
          new Uint8Array([...inviteNonce, ...inviteCiphertext])
        );

        // Create invite object
        const invite = {
          version: 'v1' as const,
          spaceId: space.id,
          spaceName: space.name,
          passphrase: invitePassphrase,
          tables: space.tables,
          inviterName: identity.name,
          inviterKey: identity.publicKey,
          encryptedKey: encryptedKeyForInvite,
          salt: cryptoService.bytesToBase64(inviteSalt),
          keyCheck: space.keyCheck,
          expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days
          signature: '', // TODO: Sign with identity key
        };

        // Encode as URL-safe base64
        const inviteJson = JSON.stringify(invite);
        const inviteBase64 = btoa(inviteJson);
        const inviteUrl = `ceocc://join/${inviteBase64}`;

        return inviteUrl;
      },

      // ============================================
      // SYNC (Phase 2)
      // ============================================

      startSync: async () => {
        // TODO: Implement in Phase 2
        set({ status: 'connected' });
      },

      stopSync: () => {
        set({ status: 'disconnected', connectedPeers: new Map() });
      },

      // ============================================
      // UTILITY
      // ============================================

      setError: (error: string | null) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'ceocc-sync-store',
      // Only persist these fields
      partialize: (state) => ({
        activeSpaceId: state.activeSpaceId,
      }),
    }
  )
);

// ============================================
// SELECTORS
// ============================================

export const selectInitialized = (state: SyncStore) => state.initialized;
export const selectIdentity = (state: SyncStore) => state.identity;
export const selectUnlocked = (state: SyncStore) => state.unlocked;
export const selectSpaces = (state: SyncStore) => state.spaces;
export const selectActiveSpace = (state: SyncStore) => state.activeSpace;
export const selectActiveSpaceId = (state: SyncStore) => state.activeSpaceId;
export const selectSyncStatus = (state: SyncStore) => state.status;
export const selectError = (state: SyncStore) => state.error;
export const selectNeedsSetup = (state: SyncStore) =>
  state.initialized && !state.identity;
export const selectNeedsUnlock = (state: SyncStore) =>
  state.initialized && state.identity && !state.unlocked;
export const selectReady = (state: SyncStore) =>
  state.initialized && state.identity && state.unlocked && state.activeSpace;
